/**
 * Collections & User Values API Client
 * 
 * Handles API endpoints for:
 * - User Shareable Collections (create, view, share, manage products)
 * - User Value Preferences (save values/regions for USA/Canada context)
 * - Products by Values (personalized product recommendations)
 * 
 * @author OriginSelect Team
 * @version 1.0.0
 * @created December 1, 2025
 */

import axios from 'axios';
import API from '../api'; // Import main API client for proxy support
import {
  clearStoredGoogleSession,
  getStoredGoogleIdToken,
} from '../utils/googleAuth';

// Environment detection FIRST
const isDevelopment = process.env.NODE_ENV === 'development';
const awsServerUrl = 'https://d3d8378t728e1f.cloudfront.net';
const apiBaseURL = process.env.REACT_APP_API_BASE_URL || awsServerUrl;

// Use CloudFront directly — the dev proxy is broken (returns S3 HTML instead of JSON)
// CloudFront has CORS headers (Access-Control-Allow-Origin: *) so direct calls work fine
const profileBaseURL = apiBaseURL;

console.log('🔐 [ProfileAPI] Configuration:', {
  isDevelopment,
  baseURL: apiBaseURL,
  note: 'Using CloudFront directly (bypasses broken dev proxy)'
});

const ProfileAPI = axios.create({
  baseURL: profileBaseURL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: false
});

// Add auth interceptor to ProfileAPI  
ProfileAPI.interceptors.request.use(config => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      // Debug: Log all localStorage keys related to auth
      const allKeys = Object.keys(localStorage);
      const authKeys = allKeys.filter(key => key.toLowerCase().includes('token') || key.toLowerCase().includes('user'));
      
      // First try to get a valid saved Google ID token
      let googleIdToken = getStoredGoogleIdToken();
      
      // If no token but user data exists, try to get token from Google
      if (!googleIdToken) {
        const userData = localStorage.getItem('userData');
        if (userData) {
          // User is logged in but no token in storage
          // Check if Google Identity library is available and try to get the credential
          if (window.google && window.google.accounts && window.google.accounts.id) {
            console.warn('⚠️ [ProfileAPI Auth] User data exists but no token - attempting to retrieve credential from Google...');
            try {
              // Try to get the stored credential (this only works if One Tap was triggered)
              const credential = window.google.accounts.id.getCredential?.();
              if (credential) {
                googleIdToken = credential;
                localStorage.setItem('userToken', googleIdToken);
                localStorage.setItem('userIdToken', googleIdToken);
                console.log('✅ [ProfileAPI Auth] Retrieved credential from Google');
              }
            } catch (e) {
              console.warn('Could not retrieve credential from Google:', e);
            }
          }
        }
      }
      
      console.log('🔐 [ProfileAPI Auth Debug] Token check:', {
        hasToken: !!googleIdToken,
        tokenLength: googleIdToken ? googleIdToken.length : 0,
        tokenPreview: googleIdToken ? googleIdToken.substring(0, 20) + '...' : 'NO TOKEN',
        authKeys: authKeys.length
      });
      
      if (googleIdToken) {
        config.headers = config.headers || {};
        config.headers['Authorization'] = `Bearer ${googleIdToken}`;
        console.log('🔐 [ProfileAPI Auth] Added Authorization header with Google ID token');
      } else {
        console.warn('⚠️ [ProfileAPI Auth] No valid Google ID token available - authenticated request skipped');
        console.warn('🔐 Available auth keys:', authKeys);
      }
    }
  } catch (e) {
    console.warn('Could not retrieve auth token for ProfileAPI:', e);
  }
  return config;
});

// Response interceptors for both dev and prod
ProfileAPI.interceptors.response.use(
  response => {
    console.log('🔐 [ProfileAPI] Success Response:', {
      status: response.status,
      url: response.config.url,
      responseData: response.data
    });
    return response;
  },
  error => {
    // Check if backend returned HTML instead of JSON (routing issue)
    const isHtmlResponse = typeof error.response?.data === 'string' && 
                          (error.response.data.includes('<!DOCTYPE') || 
                           error.response.data.includes('<html'));
    
    if (isHtmlResponse) {
      // Extract error message from HTML if possible
      const errorMatch = error.response.data.match(/<pre>(.*?)<\/pre>/s);
      const backendError = errorMatch ? errorMatch[1].substring(0, 200) : 'HTML response received';
      
      console.error('🔐 [ProfileAPI] Backend Error (HTML response):', {
        status: error.response?.status,
        url: error.config?.url,
        backendErrorSnippet: backendError,
        note: 'Backend returned HTML instead of JSON - check if endpoint exists and is routing correctly',
        requestPayload: error.config?.data
      });
    } else {
      console.error('🔐 [ProfileAPI] Error Response:', {
        status: error.response?.status,
        message: error.message,
        url: error.config?.url,
        responseData: error.response?.data,
        requestData: error.config?.data ? { ...error.config.data, profile_pic_base64: error.config.data.profile_pic_base64 ? '[BASE64_DATA]' : undefined } : 'none'
      });
    }
    return Promise.reject(error);
  }
);

// Create axios instance for Collections API
// Use CloudFront directly — the dev proxy returns S3 HTML instead of JSON
// CloudFront has CORS headers and routes /api/* to backend properly
const CollectionsAPI = axios.create({
  baseURL: apiBaseURL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: false
});

// Add auth interceptor to CollectionsAPI to send Google ID token
CollectionsAPI.interceptors.request.use(config => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      // Get a valid Google ID token (stored by One Tap or credential flow)
      const googleIdToken = getStoredGoogleIdToken();
      
      // Debug: Check localStorage
      const allKeys = Object.keys(localStorage);
      const authKeys = allKeys.filter(key => key.toLowerCase().includes('token') || key.toLowerCase().includes('user'));
      
      if (googleIdToken) {
        // Add Authorization header with Bearer token for backend authentication
        config.headers = config.headers || {};
        config.headers['Authorization'] = `Bearer ${googleIdToken}`;
        console.log('📚 [CollectionsAPI Auth] Added Authorization header with Google ID token');
      } else {
        console.warn('⚠️ [CollectionsAPI Auth] No valid Google ID token found in localStorage');
        console.warn('📚 [CollectionsAPI Auth] Available keys:', authKeys);
      }
    }
  } catch (e) {
    console.warn('Could not retrieve auth token for CollectionsAPI:', e);
  }
  return config;
});

// Add response interceptor to handle 401 and attempt token recovery
CollectionsAPI.interceptors.response.use(
  response => response,
  async error => {
    // If we get 401, check for requiresLogin flag from backend
    if (error.response?.status === 401 && typeof window !== 'undefined') {
      const responseData = error.response?.data;
      const existingToken = getStoredGoogleIdToken();
      const userData = localStorage.getItem('user');
      
      console.error('❌ [CollectionsAPI] Got 401 Unauthorized');
      console.error('📊 [CollectionsAPI] Response data:', responseData);
      console.error('📊 [CollectionsAPI] Current localStorage state:', {
        hasUserToken: !!existingToken,
        hasUserData: !!userData,
        userEmail: userData ? JSON.parse(userData).email : null,
        allKeys: Object.keys(localStorage)
      });
      
      // Check if backend explicitly says login is required
      if (responseData?.requiresLogin === true) {
        console.warn('⚠️ [CollectionsAPI] Backend requires login - clearing expired token');
        clearStoredGoogleSession();
        
        // Enhance error with requiresLogin flag for UI components
        const authError = new Error(responseData?.message || 'Your session has expired. Please log in again to continue.');
        authError.requiresLogin = true;
        authError.originalError = error;
        return Promise.reject(authError);
      }
      
      if (!existingToken && userData) {
        console.warn('⚠️ [CollectionsAPI] Token missing but user data exists - user needs to sign in again');
      }
    }
    
    return Promise.reject(error);
  }
);

// Debug logging in development
if (isDevelopment) {
  CollectionsAPI.interceptors.request.use(config => {
    console.log('📚 [CollectionsAPI] Request:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      params: config.params,
      hasUserId: !!(config.data?.userId || config.params?.userId),
      dataSize: config.data ? `${JSON.stringify(config.data).length} bytes` : 'none'
    });
    return config;
  });

  CollectionsAPI.interceptors.response.use(
    response => {
      console.log('📚 [CollectionsAPI] Response:', {
        status: response.status,
        url: response.config.url,
        data: response.data
      });
      return response;
    },
    error => {
      console.error('📚 [CollectionsAPI] Error:', {
        status: error.response?.status,
        message: error.message,
        url: error.config?.url
      });
      return Promise.reject(error);
    }
  );
}

/**
 * =========================================
 * VALUES DEFINITIONS API
 * =========================================
 */

/**
 * Get all value definitions
 * @param {Object} params - Query parameters
 * @param {string} params.country - Filter by country: 'USA' or 'CA'
 * @param {string} params.group - Filter by group: 'ownership', 'sustainability', 'social'
 * @param {boolean} params.primary - If true, return only primary values
 * @returns {Promise<Object>} Values definitions
 */
export const fetchValuesDefinitions = async (params = {}) => {
  try {
    const response = await CollectionsAPI.get('/api/values-definitions', { params });
    return response.data;
  } catch (error) {
    console.error('fetchValuesDefinitions failed:', error.message);
    throw error;
  }
};

/**
 * Get primary values for USA (15 values)
 * @deprecated Use publicApi.fetchUSAPrimaryValues instead
 * @returns {Promise<Object>} USA primary values
 */
export const fetchUSAPrimaryValues = async () => {
  // Redirect to publicApi
  const publicApi = require('./publicApi').default;
  return publicApi.get('/api/values-definitions/primary/usa').then(r => r.data);
};

/**
 * Get primary values for Canada (10 values)
 * @deprecated Use publicApi.fetchCanadaPrimaryValues instead
 * @returns {Promise<Object>} Canada primary values
 */
export const fetchCanadaPrimaryValues = async () => {
  // Redirect to publicApi
  const publicApi = require('./publicApi').default;
  return publicApi.get('/api/values-definitions/primary/canada').then(r => r.data);
};

/**
 * Get all value groups
 * @returns {Promise<Object>} Value groups
 */
export const fetchValueGroups = async () => {
  try {
    const response = await CollectionsAPI.get('/api/values-definitions/groups');
    return response.data;
  } catch (error) {
    console.error('fetchValueGroups failed:', error.message);
    throw error;
  }
};

/**
 * Get single value definition
 * @param {string} valueCode - Value code (e.g., 'women_owned')
 * @param {string} country - Optional country for localized labels ('CA' or 'USA')
 * @returns {Promise<Object>} Value definition
 */
export const fetchValueDefinition = async (valueCode, country = null) => {
  try {
    const params = country ? { country } : {};
    const response = await CollectionsAPI.get(`/api/values-definitions/${valueCode}`, { params });
    return response.data;
  } catch (error) {
    console.error(`fetchValueDefinition failed for ${valueCode}:`, error.message);
    throw error;
  }
};

/**
 * =========================================
 * REGIONS API
 * =========================================
 */

/**
 * Get all countries and their regions
 * @param {string} country - Optional filter by country: 'USA' or 'CA'
 * @returns {Promise<Object>} Countries and regions data
 */
export const fetchRegions = async (country = null) => {
  try {
    const params = country ? { country } : {};
    const response = await CollectionsAPI.get('/api/regions', { params });
    return response.data;
  } catch (error) {
    console.error('fetchRegions failed:', error.message);
    throw error;
  }
};

/**
 * Get list of supported countries
 * @returns {Promise<Object>} Countries list
 */
export const fetchCountries = async () => {
  try {
    const response = await CollectionsAPI.get('/api/regions/countries');
    return response.data;
  } catch (error) {
    console.error('fetchCountries failed:', error.message);
    throw error;
  }
};

/**
 * Get all US states
 * @deprecated Use publicApi.fetchUSAStates instead
 * @returns {Promise<Object>} US states list
 */
export const fetchUSAStates = async () => {
  // Redirect to publicApi
  const publicApi = require('./publicApi').default;
  return publicApi.get('/api/regions/usa').then(r => r.data);
};

/**
 * Get all Canadian provinces
 * @deprecated Use publicApi.fetchCanadianProvinces instead
 * @returns {Promise<Object>} Canadian provinces list
 */
export const fetchCanadianProvinces = async () => {
  // Redirect to publicApi
  const publicApi = require('./publicApi').default;
  return publicApi.get('/api/regions/canada').then(r => r.data);
};

/**
 * =========================================
 * USER PREFERENCES API
 * =========================================
 */

/**
 * Get user's value preferences
 * @param {string} userId - User's email
 * @param {string} context - 'CA', 'USA', or 'all'
 * @returns {Promise<Object>} User preferences
 */
export const fetchUserPreferences = async (userId, context = 'USA') => {
  try {
    const response = await CollectionsAPI.get('/api/me/preferences/values', {
      params: { userId, context }
    });
    return response.data;
  } catch (error) {
    console.error('fetchUserPreferences failed:', error.message);
    throw error;
  }
};

/**
 * Replace all preferences for a specific context
 * @param {Object} data - Preferences data
 * @param {string} data.userId - User's email
 * @param {string} data.context - 'CA' or 'USA'
 * @param {string[]} data.preferredValues - Array of value codes
 * @param {string[]} data.preferredRegions - Array of region codes
 * @returns {Promise<Object>} Updated preferences
 */
export const updateUserPreferences = async (data) => {
  try {
    const response = await CollectionsAPI.put('/api/me/preferences/values', data);
    return response.data;
  } catch (error) {
    console.error('updateUserPreferences failed:', error.message);
    throw error;
  }
};

/**
 * Partially update preferences (add/remove values)
 * @param {Object} data - Partial update data
 * @param {string} data.userId - User's email
 * @param {string} data.context - 'CA' or 'USA'
 * @param {string[]} data.addValues - Values to add
 * @param {string[]} data.removeValues - Values to remove
 * @param {string[]} data.preferredRegions - New regions list (replaces existing)
 * @returns {Promise<Object>} Updated preferences
 */
export const patchUserPreferences = async (data) => {
  try {
    const response = await CollectionsAPI.patch('/api/me/preferences/values', data);
    return response.data;
  } catch (error) {
    console.error('patchUserPreferences failed:', error.message);
    throw error;
  }
};

/**
 * Clear user preferences
 * @param {string} userId - User's email
 * @param {string} context - 'CA', 'USA', or 'all'
 * @returns {Promise<Object>} Success response
 */
export const clearUserPreferences = async (userId, context = 'all') => {
  try {
    const response = await CollectionsAPI.delete('/api/me/preferences/values', {
      params: { userId, context }
    });
    return response.data;
  } catch (error) {
    console.error('clearUserPreferences failed:', error.message);
    throw error;
  }
};

/**
 * =========================================
 * COLLECTIONS API
 * =========================================
 */

/**
 * Create a new collection
 * @param {Object} data - Collection data
 * @param {string} data.userId - User's email
 * @param {string} data.title - Collection title
 * @param {string} data.description - Optional description
 * @param {boolean} data.isPublic - Whether publicly accessible (default: true)
 * @param {string} data.coverImageUrl - Optional cover image URL
 * @param {string[]} data.initialProductIds - Optional products to add immediately
 * @returns {Promise<Object>} Created collection
 */
export const createCollection = async (data) => {
  try {
    const response = await CollectionsAPI.post('/api/collections', data);
    return response.data;
  } catch (error) {
    console.error('createCollection failed:', error.message);
    throw error;
  }
};

/**
 * Get all collections for a user
 * @param {string} userId - User's email
 * @param {string} market - Optional market filter: 'canada' or 'global'
 * @returns {Promise<Object>} User's collections
 */
export const fetchUserCollections = async (userId, market = null) => {
  try {
    const params = { userId };
    if (market) {
      params.market = market;
    }
    // Add cache-busting parameter to prevent CloudFront from serving cached responses
    // This ensures each user gets their own collections, not another user's cached response
    params._t = Date.now();
    
    const response = await CollectionsAPI.get('/api/collections/me', {
      params
    });
    return response.data;
  } catch (error) {
    console.error('fetchUserCollections failed:', error.message);
    throw error;
  }
};

/**
 * Get a single collection by ID (owner view)
 * @param {string} collectionId - Collection ID
 * @param {string} userId - User's email (for permission check)
 * @returns {Promise<Object>} Collection with products
 */
export const fetchCollection = async (collectionId, userId) => {
  try {
    const response = await CollectionsAPI.get(`/api/collections/${collectionId}`, {
      params: { userId }
    });
    return response.data;
  } catch (error) {
    console.error(`fetchCollection failed for ${collectionId}:`, error.message);
    throw error;
  }
};

/**
 * Update collection metadata
 * @param {string} collectionId - Collection ID
 * @param {Object} data - Update data
 * @param {string} data.userId - User's email
 * @param {string} data.title - New title
 * @param {string} data.description - New description
 * @param {boolean} data.isPublic - New visibility
 * @param {string} data.coverImageUrl - New cover image
 * @returns {Promise<Object>} Updated collection
 */
export const updateCollection = async (collectionId, data) => {
  try {
    const response = await CollectionsAPI.patch(`/api/collections/${collectionId}`, data);
    return response.data;
  } catch (error) {
    console.error(`updateCollection failed for ${collectionId}:`, error.message);
    throw error;
  }
};

/**
 * Delete a collection (soft delete)
 * @param {string} collectionId - Collection ID
 * @param {string} userId - User's email
 * @returns {Promise<Object>} Success response
 */
export const deleteCollection = async (collectionId, userId) => {
  try {
    console.log('🗑️ Deleting collection:', { collectionId, userId });
    const response = await CollectionsAPI.delete(`/api/collections/${collectionId}`, {
      params: { userId }
    });
    return response.data;
  } catch (error) {
    console.error(`deleteCollection failed for ${collectionId}:`, {
      status: error.response?.status,
      data: error.response?.data,
      message: error.message
    });
    throw error;
  }
};

/**
 * Add a product to a collection
 * @param {string} collectionId - Collection ID
 * @param {Object} data - Product data
 * @param {string} data.userId - User's email
 * @param {string} data.productId - Product ID to add
 * @param {string} data.note - Optional note about the product
 * @param {number} data.position - Optional position in collection
 * @returns {Promise<Object>} Updated collection
 */
export const addProductToCollection = async (collectionId, data) => {
  try {
    const response = await CollectionsAPI.post(`/api/collections/${collectionId}/items`, data);
    return response.data;
  } catch (error) {
    console.error(`addProductToCollection failed:`, error.message);
    throw error;
  }
};

/**
 * Remove a product from a collection
 * @param {string} collectionId - Collection ID
 * @param {string} productId - Product ID to remove
 * @param {string} userId - User's email
 * @returns {Promise<Object>} Success response
 */
export const removeProductFromCollection = async (collectionId, productId, userId) => {
  try {
    const response = await CollectionsAPI.delete(
      `/api/collections/${collectionId}/items/${productId}`,
      { params: { userId } }
    );
    return response.data;
  } catch (error) {
    console.error(`removeProductFromCollection failed:`, error.message);
    throw error;
  }
};

/**
 * Update a collection item (note, position)
 * @param {string} collectionId - Collection ID
 * @param {string} productId - Product ID
 * @param {Object} data - Update data
 * @param {string} data.userId - User's email
 * @param {string} data.note - Updated note
 * @param {number} data.position - Updated position
 * @returns {Promise<Object>} Updated item
 */
export const updateCollectionItem = async (collectionId, productId, data) => {
  try {
    const response = await CollectionsAPI.patch(
      `/api/collections/${collectionId}/items/${productId}`,
      data
    );
    return response.data;
  } catch (error) {
    console.error(`updateCollectionItem failed:`, error.message);
    throw error;
  }
};

/**
 * Reorder products in a collection
 * @param {string} collectionId - Collection ID
 * @param {Object} data - Reorder data
 * @param {string} data.userId - User's email
 * @param {string[]} data.itemOrder - Array of product IDs in new order
 * @returns {Promise<Object>} Success response
 */
export const reorderCollectionItems = async (collectionId, data) => {
  try {
    if (!data.userId) {
      throw new Error('userId is required for reordering');
    }

    console.log('🔄 [reorderCollectionItems] Reordering collection:', {
      collectionId,
      userId: data.userId,
      itemOrder: data.itemOrder,
      itemCount: data.itemOrder?.length
    });
    
    // Use API which goes directly to the backend (not through dev proxy)
    // Backend should return proper JSON response
    const response = await CollectionsAPI.patch(
      `/api/collections/${collectionId}/items/reorder`,
      {
        userId: data.userId,
        itemOrder: data.itemOrder
      }
    );
    
    console.log('✅ [reorderCollectionItems] Response:', { 
      status: response.status,
      statusText: response.statusText,
      data: response.data
    });
    
    return response.data;
  } catch (error) {
    console.error(`❌ [reorderCollectionItems] Failed:`, error.message);
    console.error('Error details:', {
      status: error.response?.status,
      data: error.response?.data,
      message: error.message,
      url: error.config?.url
    });
    throw error;
  }
};

/**
 * =========================================
 * PUBLIC COLLECTIONS API
 * =========================================
 */

/**
 * Get a public collection by slug and short ID
 * @param {string} slugAndId - Combined slug and shortId (e.g., 'winter-skincare-favorites-ab12cd34')
 * @param {string} userId - Optional user email (allows owner to view private collections)
 * @returns {Promise<Object>} Public collection
 */
export const fetchPublicCollection = async (slugAndId, userId = null) => {
  try {
    const params = userId ? { userId } : {};
    // Add cache-busting parameter to bypass CloudFront cache
    // This ensures fresh data after collection updates
    params._t = Date.now();
    
    console.log('📚 [fetchPublicCollection] Requesting:', { 
      endpoint: `/api/public/collections/${slugAndId}`,
      params,
      userId 
    });
    const response = await API.get(`/api/public/collections/${slugAndId}`, { params });
    console.log('📚 [fetchPublicCollection] Full Response:', response.data);
    console.log('📚 [fetchPublicCollection] isOwner value:', response.data.isOwner);
    console.log('📚 [fetchPublicCollection] Collection owner:', {
      ownerId: response.data.collection?.ownerId,
      userId: response.data.collection?.userId,
      requestedUserId: userId
    });
    return response.data;
  } catch (error) {
    console.error(`fetchPublicCollection failed for ${slugAndId}:`, error.message);
    if (error.response) {
      console.error('📚 [fetchPublicCollection] Error response:', {
        status: error.response.status,
        data: error.response.data
      });
    }
    throw error;
  }
};

/**
 * =========================================
 * PRODUCTS BY VALUES API
 * =========================================
 */

/**
 * Get products matching user's saved value preferences
 * @param {Object} params - Query parameters
 * @param {string} params.userId - User's email
 * @param {string} params.context - 'CA' or 'USA' (default: 'USA')
 * @param {number} params.limit - Products to return (default: 24)
 * @param {string} params.nextToken - Pagination token
 * @param {string} params.matchMode - 'any' (OR) or 'all' (AND)
 * @returns {Promise<Object>} Matched products with pagination
 */
export const fetchProductsByValues = async (params = {}) => {
  try {
    const response = await CollectionsAPI.get('/api/me/products-by-values', { params });
    return response.data;
  } catch (error) {
    console.error('fetchProductsByValues failed:', error.message);
    throw error;
  }
};

/**
 * =========================================
 * USER SAVED COLLECTIONS API
 * =========================================
 */

/**
 * Save a public collection to the user's saved list
 * @param {string} collectionId - Collection ID to save
 * @param {string} userId - User's email
 * @returns {Promise<Object>} Saved collection record
 */
export const saveCollection = async (collectionId, userId) => {
  try {
    const response = await CollectionsAPI.post('/api/user-collections', {
      collectionId,
      userId
    });
    return response.data;
  } catch (error) {
    console.error('saveCollection failed:', error.message);
    throw error;
  }
};

/**
 * Get all saved collections for a user
 * @param {string} userId - User's email
 * @param {boolean} includeDetails - Whether to include full collection details
 * @returns {Promise<Object>} Saved collections
 */
export const fetchSavedCollections = async (userId, includeDetails = true) => {
  try {
    const params = { userId };
    if (includeDetails) {
      params.include = 'collections';
    }
    // Add cache-busting parameter to prevent CloudFront caching issues
    params._t = Date.now();
    
    const response = await CollectionsAPI.get('/api/user-collections', { params });
    return response.data;
  } catch (error) {
    console.error('fetchSavedCollections failed:', error.message);
    throw error;
  }
};

/**
 * Check if a specific collection is saved by the user
 * @param {string} collectionId - Collection ID to check (can be MongoDB ObjectId or slug-shortId format)
 * @param {string} userId - User's email
 * @returns {Promise<boolean>} Whether the collection is saved
 */
export const isCollectionSaved = async (collectionId, userId) => {
  try {
    // Fetch user's saved collections and check if this one is in the list
    const response = await CollectionsAPI.get('/api/user-collections', {
      params: { userId, include: 'collections' }
    });
    // API returns { items: [...] }
    const savedItems = response.data.items || response.data.savedCollections || response.data || [];
    console.log('isCollectionSaved check:', { 
      collectionId, 
      savedItems: savedItems.map(i => ({
        collectionId: i.collectionId,
        id: i.id,
        collectionObjId: i.collection?.id,
        collectionObjSlug: i.collection?.slug,
        collectionObjShortId: i.collection?.shortId,
        fullSlug: i.collection?.slug && i.collection?.shortId 
          ? `${i.collection.slug}-${i.collection.shortId}` 
          : null
      }))
    });
    
    // Check if the collectionId exists in the saved list (check multiple possible fields)
    // Also handle slug-shortId format (e.g., "collection-title-slug-abc12345")
    return savedItems.some(item => {
      // Direct ID matches
      if (item.collectionId === collectionId || 
          item.collection?.id === collectionId ||
          item.collection?._id === collectionId ||
          item.id === collectionId ||
          item._id === collectionId) {
        return true;
      }
      
      // Slug matches (exact slug match)
      if (item.collection?.slug === collectionId) {
        return true;
      }
      
      // Check if collectionId is a slug-shortId format that matches
      // Example: "collection-title-slug-abc12345" where "abc12345" is the shortId
      if (item.collection?.slug && item.collection?.shortId) {
        const fullSlug = `${item.collection.slug}-${item.collection.shortId}`;
        if (fullSlug === collectionId) {
          return true;
        }
      }
      
      // Also check if the collectionId ends with the shortId (backwards compat)
      if (item.collection?.shortId && collectionId.endsWith(item.collection.shortId)) {
        return true;
      }
      
      return false;
    });
  } catch (error) {
    console.error('isCollectionSaved failed:', error.message);
    return false;
  }
};

/**
 * Check saved status for multiple collections at once
 * @param {string[]} collectionIds - Array of collection IDs to check
 * @param {string} userId - User's email
 * @returns {Promise<Object>} Map of collectionId -> boolean
 */
export const checkMultipleCollectionsSaved = async (collectionIds, userId) => {
  try {
    const response = await CollectionsAPI.post('/api/user-collections/check-saved', {
      collectionIds,
      userId
    });
    return response.data.savedStatus;
  } catch (error) {
    console.error('checkMultipleCollectionsSaved failed:', error.message);
    return {};
  }
};

/**
 * Unsave a collection from the user's saved list
 * @param {string} collectionId - Collection ID to unsave
 * @param {string} userId - User's email
 * @returns {Promise<void>}
 */
export const unsaveCollection = async (collectionId, userId) => {
  try {
    await CollectionsAPI.delete('/api/user-collections', {
      params: { collectionId, userId }
    });
  } catch (error) {
    console.error('unsaveCollection failed:', error.message);
    throw error;
  }
};

/**
 * =========================================
 * CURATOR PROFILE
 * =========================================
 */

/**
 * Update curator profile (first name, last name, profile picture)
 * @param {string} userId - User ID or email
 * @param {Object} data - Profile update data
 * @param {string} data.name - Updated full name (or firstName + lastName)
 * @param {string} data.firstName - Updated first name
 * @param {string} data.lastName - Updated last name
 * @param {File} data.profile_pic_file - File object for image upload (S3 presigned URL)
 * @param {boolean} data.removeProfilePic - Flag to remove profile picture
 * @returns {Promise<Object>} Updated profile data
 * 
 * Process:
 * 1. Update name/text fields
 * 2a. Request presigned URL from backend
 * 2b. Upload image directly to S3
 * 2c. Update profile with S3 URL
 */
export const updateCuratorProfile = async (userId, data) => {
  let successfulUpdates = {
    name: false,
    image: false
  };
  
  try {
    // Step 1: Update name/text fields first (small payload, no CORS issues)
    if (data.name || data.firstName || data.lastName) {
      const textPayload = {
        userId: userId,
        firstName: data.firstName,
        lastName: data.lastName,
        name: data.name
      };
      
      console.log('📤 Step 1: Updating curator profile text fields:', { userId, name: data.name });
      
      try {
        const textResponse = await ProfileAPI.patch('/api/me/profile', textPayload);
        console.log('✅ Step 1 complete: Text fields updated', { 
          status: textResponse.status,
          responseData: textResponse.data
        });
        console.log('📝 FULL API RESPONSE:', JSON.stringify(textResponse.data, null, 2));
        successfulUpdates.name = true;
      } catch (err) {
        console.error('❌ Step 1 failed: Name update error:', {
          message: err.message,
          status: err.response?.status,
          responseData: err.response?.data
        });
        // Don't throw here - continue with image upload if it exists
      }
    }
    
    // Step 2: Handle image upload (backend handles S3 upload server-side)
    if (data.profile_pic_file || data.removeProfilePic) {
      if (data.removeProfilePic) {
        // Remove profile picture
        console.log('📤 Step 2: Removing profile picture');
        const removePayload = {
          userId,
          removeProfilePic: true
        };
        
        try {
          const removeResponse = await ProfileAPI.patch('/api/me/profile', removePayload);
          console.log('✅ Step 2 complete: Profile picture removed', { status: removeResponse.status });
          successfulUpdates.image = true;
        } catch (err) {
          console.error('❌ Step 2 failed: Remove image error:', {
            message: err.message,
            status: err.response?.status,
            responseData: err.response?.data
          });
        }
      } else if (data.profile_pic_file) {
        // Use presigned URL for direct S3 upload (bypasses CloudFront)
        const file = data.profile_pic_file;
        console.log(`📤 Step 2: Getting presigned URL for upload (${(file.size / 1024).toFixed(2)}KB)...`);
        
        try {
          // Step 2a: Get presigned URL from backend (same pattern as brand registration)
          const uploadUrlResponse = await ProfileAPI.post('/api/me/profile/upload-url', {
            userId: userId,
            fileName: file.name,
            fileType: file.type || 'image/jpeg',
            fileSize: file.size
          });
          
          if (!uploadUrlResponse.data || !uploadUrlResponse.data.data || !uploadUrlResponse.data.data.uploadUrl) {
            throw new Error('Failed to get presigned URL from backend');
          }
          
          const { uploadUrl, s3Url } = uploadUrlResponse.data.data;
          console.log('✅ Step 2a complete: Presigned URL obtained');
          console.log('📤 Step 2b: Uploading directly to S3 (bypassing CloudFront)...');
          
          // Step 2b: Upload file directly to S3 using presigned URL (NOT through CloudFront)
          const s3Response = await fetch(uploadUrl, {
            method: 'PUT',
            body: file,
            headers: {
              'Content-Type': file.type || 'image/jpeg'
            }
          });
          
          if (!s3Response.ok) {
            throw new Error(`S3 upload failed with status ${s3Response.status}: ${s3Response.statusText}`);
          }
          
          console.log('✅ Step 2b complete: File uploaded directly to S3');
          console.log('📤 Step 3: Updating profile with S3 URL...');
          
          // Step 3: Update profile with the S3 URL
          const profileUpdatePayload = {
            userId,
            profile_pic: s3Url
          };
          
          const profileUpdateResponse = await ProfileAPI.patch('/api/me/profile', profileUpdatePayload);
          
          console.log('✅ Step 3 complete: Profile updated with image URL', { 
            status: profileUpdateResponse.status,
            s3Url
          });
          
          successfulUpdates.image = true;
          successfulUpdates.profilePicUrl = s3Url;
          
          console.log('✅ Profile picture uploaded successfully:', s3Url);
          
        } catch (err) {
          console.error('❌ Image upload failed:', {
            message: err.message,
            status: err.response?.status,
            statusText: err.response?.statusText,
            responseDataType: typeof err.response?.data,
            responseData: err.response?.data
          });
          
          // Handle different error response types
          let errorMessage = err.message;
          if (err.response?.data) {
            if (typeof err.response.data === 'string') {
              errorMessage = err.response.data;
            } else if (err.response.data.message) {
              errorMessage = err.response.data.message;
            } else if (err.response.data.error) {
              errorMessage = err.response.data.error;
            }
          }
          
          throw new Error(`Failed to upload image: ${errorMessage}`);
        }
      }
    }
    
    // Return success if at least name update succeeded
    if (successfulUpdates.name || successfulUpdates.image) {
      const resultUser = {
        userId
      };
      
      // Add name fields if name was updated
      if (successfulUpdates.name) {
        resultUser.firstName = data.firstName;
        resultUser.lastName = data.lastName;
        resultUser.name = data.name;
      }
      
      // Add profile_pic if image was updated
      if (successfulUpdates.profilePicUrl) {
        resultUser.profile_pic = successfulUpdates.profilePicUrl;
      }
      
      console.log('✅ Curator profile updated successfully', { successfulUpdates, resultUser });
      return {
        success: true,
        message: 'Profile updated successfully',
        user: resultUser
      };
    } else {
      throw new Error('No updates were applied');
    }
  } catch (error) {
    console.error('❌ updateCuratorProfile failed:', {
      message: error.message,
      status: error.response?.status,
      responseData: error.response?.data
    });
    
    // Provide helpful error messages
    if (error.response?.status === 401) {
      const responseData = error.response?.data;
      const errorMessage = responseData?.message || 'Unauthorized - Please log in again';
      
      // Clear expired token if backend indicates requiresLogin
      if (responseData?.requiresLogin === true && typeof window !== 'undefined') {
        localStorage.removeItem('userToken');
        console.log('✅ Cleared expired token from localStorage');
      }
      
      throw new Error(errorMessage);
    } else if (error.response?.status === 403) {
      throw new Error('Forbidden - You do not have permission to update this profile');
    } else if (error.response?.status === 404) {
      throw new Error('Profile not found');
    } else if (error.response?.status === 413) {
      throw new Error('Image is too large - please choose a smaller image');
    } else if (error.response?.status >= 500) {
      throw new Error('Server error - please try again later');
    }
    
    throw error;
  }
};

/**
 * =========================================
 * UTILITY FUNCTIONS
 * =========================================
 */

/**
 * Generate share URL for a collection
 * Always uses production domain for social sharing to work correctly
 * @param {Object} collection - Collection object with publicUrl and market
 * @returns {string} Full shareable URL
 */
export const generateShareUrl = (collection) => {
  // Always use production URL for sharing (social media links won't work with localhost)
  const baseUrl = 'https://www.originselect.com';
  
  // Determine market path based on collection's market field
  const marketPath = collection.market === 'global' ? '/global' : '/country/canada';
  
  // If publicUrl already has the market path, use it directly
  if (collection.publicUrl?.startsWith('/country/') || collection.publicUrl?.startsWith('/global/')) {
    return `${baseUrl}${collection.publicUrl}`;
  }
  
  // Otherwise, construct the URL with market path
  const slug = collection.slug || collection.title?.toLowerCase().replace(/\s+/g, '-');
  const shortId = collection.shortId || collection.id?.slice(-8);
  
  return `${baseUrl}${marketPath}/collections/${slug}-${shortId}`;
};

/**
 * Copy collection URL to clipboard
 * @param {Object} collection - Collection object with publicUrl
 * @returns {Promise<boolean>} Success status
 */
export const copyCollectionUrl = async (collection) => {
  try {
    const url = generateShareUrl(collection);
    await navigator.clipboard.writeText(url);
    return true;
  } catch (error) {
    console.error('Failed to copy URL:', error);
    return false;
  }
};

/**
 * Validate value codes against known values
 * @param {string[]} valueCodes - Array of value codes to validate
 * @param {string} country - 'USA' or 'CA'
 * @returns {Promise<Object>} Validation result with valid/invalid codes
 */
export const validateValueCodes = async (valueCodes, country = 'USA') => {
  try {
    const endpoint = country === 'CA' 
      ? '/api/values-definitions/primary/canada'
      : '/api/values-definitions/primary/usa';
    const response = await CollectionsAPI.get(endpoint);
    const validCodes = response.data.values.map(v => v.valueCode);
    
    const valid = valueCodes.filter(code => validCodes.includes(code));
    const invalid = valueCodes.filter(code => !validCodes.includes(code));
    
    return { valid, invalid, allValid: invalid.length === 0 };
  } catch (error) {
    console.error('validateValueCodes failed:', error.message);
    throw error;
  }
};

// Export default object with all methods
export default {
  // Values Definitions
  fetchValuesDefinitions,
  fetchUSAPrimaryValues,
  fetchCanadaPrimaryValues,
  fetchValueGroups,
  fetchValueDefinition,
  
  // Regions
  fetchRegions,
  fetchCountries,
  fetchUSAStates,
  fetchCanadianProvinces,
  
  // User Preferences
  fetchUserPreferences,
  updateUserPreferences,
  patchUserPreferences,
  clearUserPreferences,
  
  // Collections
  createCollection,
  fetchUserCollections,
  fetchCollection,
  updateCollection,
  deleteCollection,
  addProductToCollection,
  removeProductFromCollection,
  updateCollectionItem,
  reorderCollectionItems,
  
  // Public Collections
  fetchPublicCollection,
  
  // User Saved Collections
  saveCollection,
  fetchSavedCollections,
  isCollectionSaved,
  checkMultipleCollectionsSaved,
  unsaveCollection,
  
  // Products by Values
  fetchProductsByValues,
  
  // Curator Profile
  updateCuratorProfile,
  
  // Utilities
  generateShareUrl,
  copyCollectionUrl,
  validateValueCodes
};
